/**
 * BlockEditor — one block in a workout.
 *
 * Phase 1 supersets/circuits rebuild (2026-02-17): a block now holds
 * 1+ exercise slots. Standalone = 1 slot; Superset/Circuit = 2+ slots.
 * Exercises can be dragged within the block OR onto another block via
 * HTML5 native DnD with a custom mime type so we don't collide with
 * the block-level drag in WorkoutBuilder.
 *
 * Sparse-set membership: each exercise's sets_data uses `set_num` as the
 * explicit round number. The block header surfaces all round numbers in
 * play across the block so coaches can see which exercises run in which
 * rounds at a glance.
 */
import { useState } from "react";
import { GripVertical, Plus, ChevronUp, ChevronDown, Trash2, Layers } from "lucide-react";
import ExerciseSlotEditor from "./ExerciseSlotEditor";
import { LINK_TYPES, emptyExercise, blockRoundNumbers } from "@/lib/workoutTaxonomy";

// MIME type for cross-block exercise drag — distinct from the plain text/plain
// payload used by WorkoutBuilder for block-level reordering. Letting React/HTML5
// disambiguate at drop time means we don't have to globally coordinate state.
export const EXERCISE_DRAG_MIME = "application/x-maxsc-exercise";

export default function BlockEditor({
    category,
    block,
    index,
    onChange,
    onRemove,
    canRemove,
    onMoveUp,
    onMoveDown,
    validationError = null,
    forceActiveLevel,
}) {
    const [dropHover, setDropHover] = useState(false);

    const update = (patch) => onChange({ ...block, ...patch });

    // ─── Exercise CRUD ───────────────────────────────────────────
    const addExercise = () => {
        const next = [...(block.exercises || []), emptyExercise((block.exercises?.length || 0) + 1)];
        update({ exercises: next.map((e, i) => ({ ...e, slot: i + 1 })) });
    };

    const updateExercise = (i, nextEx) => {
        update({
            exercises: block.exercises.map((e, idx) => (idx === i ? nextEx : e)),
        });
    };

    const removeExercise = (i) => {
        if ((block.exercises?.length || 0) <= 1) return;
        const next = block.exercises.filter((_, idx) => idx !== i);
        update({ exercises: next.map((e, idx) => ({ ...e, slot: idx + 1 })) });
    };

    const moveExercise = (from, to) => {
        if (from === to || from < 0 || to < 0) return;
        if (from >= block.exercises.length || to >= block.exercises.length) return;
        const next = [...block.exercises];
        const [m] = next.splice(from, 1);
        next.splice(to, 0, m);
        update({ exercises: next.map((e, idx) => ({ ...e, slot: idx + 1 })) });
    };

    // ─── Cross-block drop target ─────────────────────────────────
    // Parent WorkoutBuilder owns the drag SOURCE (it lifts exercise objects
    // out of one block); BlockEditor owns the drop TARGET. The mime type
    // gate ensures the block-level drag (text/plain) doesn't accidentally
    // fire this handler.
    const onDragOverExercise = (e) => {
        if (![...e.dataTransfer.types].includes(EXERCISE_DRAG_MIME)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDropHover(true);
    };
    const onDragLeaveExercise = () => setDropHover(false);
    const onDropExercise = (e) => {
        setDropHover(false);
        if (![...e.dataTransfer.types].includes(EXERCISE_DRAG_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
        try {
            const payload = JSON.parse(e.dataTransfer.getData(EXERCISE_DRAG_MIME));
            // If the drag started in THIS block, ignore — the in-block
            // move-up/down buttons already handle reordering. Cross-block
            // drops are the only case the drop target handles.
            if (payload.sourceBlockIndex === index) return;
            // Dispatch upward — the parent WorkoutBuilder mutates both
            // blocks (source removal + this block's append) in one tick.
            update({
                __cross_block_drop__: {
                    sourceBlockIndex: payload.sourceBlockIndex,
                    sourceExerciseIndex: payload.sourceExerciseIndex,
                    targetBlockIndex: index,
                },
            });
        } catch { /* malformed payload, ignore */ }
    };

    const exercises = block.exercises || [];
    const rounds = blockRoundNumbers(block);
    const isLinked = block.link_type === "superset" || block.link_type === "circuit";

    return (
        <div
            data-testid={`block-${index}`}
            onDragOver={onDragOverExercise}
            onDragLeave={onDragLeaveExercise}
            onDrop={onDropExercise}
            className={`border bg-zinc-950 ${dropHover ? "border-brand-red ring-1 ring-brand-red/40" : "border-zinc-900"}`}
        >
            {/* Block header */}
            <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <span title="Drag to reorder block" className="cursor-grab text-zinc-500 hover:text-brand-red">
                        <GripVertical className="w-4 h-4" />
                    </span>
                    <span className="inline-flex items-center justify-center w-9 h-9 bg-brand-red text-white font-display text-base">
                        <Layers className="w-4 h-4" />
                    </span>
                    <input
                        type="text"
                        data-testid={`block-${index}-name`}
                        placeholder="A"
                        value={block.name || ""}
                        onChange={(e) => update({ name: e.target.value, block_group: e.target.value?.[0] || block.block_group })}
                        className={`bg-transparent border-b px-1 py-1 text-2xl font-display tracking-wider text-white focus:outline-none focus:border-brand-red w-20 ${validationError?.blockNameMissing ? "border-brand-red" : "border-zinc-800"}`}
                    />
                    <select
                        data-testid={`block-${index}-link-type`}
                        value={block.link_type || ""}
                        onChange={(e) => update({ link_type: e.target.value || null, isCircuit: e.target.value === "circuit" })}
                        className="bg-black border border-zinc-800 px-2 py-1.5 text-xs text-white focus:outline-none focus:border-brand-red"
                    >
                        {LINK_TYPES.map((t) => <option key={t.value || "none"} value={t.value}>{t.label}</option>)}
                    </select>
                    {isLinked && exercises.length < 2 && (
                        <span className="text-[10px] tracking-[0.15em] uppercase text-amber-400">
                            Add a 2nd exercise to activate
                        </span>
                    )}
                    {isLinked && exercises.length >= 2 && rounds.length > 0 && (
                        <span className="text-[10px] tracking-[0.18em] uppercase text-emerald-400" data-testid={`block-${index}-round-summary`}>
                            {rounds.length} round{rounds.length === 1 ? "" : "s"}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {onMoveUp && (
                        <button type="button" data-testid={`block-${index}-up`} onClick={onMoveUp} title="Move up" className="text-zinc-300 hover:text-brand-red p-1">
                            <ChevronUp className="w-4 h-4" />
                        </button>
                    )}
                    {onMoveDown && (
                        <button type="button" data-testid={`block-${index}-down`} onClick={onMoveDown} title="Move down" className="text-zinc-300 hover:text-brand-red p-1">
                            <ChevronDown className="w-4 h-4" />
                        </button>
                    )}
                    {canRemove && (
                        <button type="button" data-testid={`block-${index}-remove`} onClick={onRemove} className="text-zinc-300 hover:text-brand-red text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5">
                            <Trash2 className="w-3.5 h-3.5" /> Remove block
                        </button>
                    )}
                </div>
            </div>

            {/* Coach note */}
            <div className="px-5 py-3 border-b border-zinc-900">
                <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">
                    Coach note (visible to athlete)
                </label>
                <textarea
                    rows={2}
                    data-testid={`block-${index}-note`}
                    placeholder="Cue, pace, intent…"
                    value={block.coach_note || ""}
                    onChange={(e) => update({ coach_note: e.target.value })}
                    className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red"
                />
            </div>

            {/* Round chips — surface which rounds this block uses across all exercises */}
            {isLinked && rounds.length > 0 && (
                <div className="px-5 py-2 border-b border-zinc-900 bg-zinc-900/30 flex items-center gap-2 flex-wrap" data-testid={`block-${index}-round-chips`}>
                    <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Rounds in play:</span>
                    {rounds.map((r) => (
                        <span key={r} className="text-[10px] tracking-[0.15em] uppercase border border-zinc-700 bg-black text-zinc-200 px-2 py-1">
                            Round {r}
                        </span>
                    ))}
                </div>
            )}

            {/* Exercise slots */}
            <div className="p-4 space-y-3">
                {exercises.map((ex, ei) => (
                    <div
                        key={ei}
                        draggable
                        data-testid={`block-${index}-exercise-drag-${ei}`}
                        onDragStart={(e) => {
                            e.stopPropagation();
                            e.dataTransfer.setData(
                                EXERCISE_DRAG_MIME,
                                JSON.stringify({ sourceBlockIndex: index, sourceExerciseIndex: ei }),
                            );
                            e.dataTransfer.effectAllowed = "move";
                        }}
                    >
                        <ExerciseSlotEditor
                            category={category}
                            exercise={ex}
                            slotIndex={ei}
                            blockIndex={index}
                            onChange={(next) => updateExercise(ei, next)}
                            onRemove={() => removeExercise(ei)}
                            canRemove={exercises.length > 1}
                            onMoveUp={ei > 0 ? () => moveExercise(ei, ei - 1) : null}
                            onMoveDown={ei < exercises.length - 1 ? () => moveExercise(ei, ei + 1) : null}
                            validationError={validationError?.slotIdx === ei ? validationError : null}
                            forceActiveLevel={validationError?.slotIdx === ei ? forceActiveLevel : null}
                        />
                    </div>
                ))}
                <button
                    type="button"
                    data-testid={`block-${index}-add-exercise`}
                    onClick={addExercise}
                    className="w-full border border-dashed border-zinc-700 hover:border-brand-red hover:text-brand-red text-zinc-300 text-[10px] tracking-[0.25em] uppercase py-3 inline-flex items-center justify-center gap-1.5"
                >
                    <Plus className="w-3 h-3" /> Add exercise to this block
                </button>
            </div>
        </div>
    );
}
